import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Text } from 'office-ui-fabric-react';
import get from 'lodash/get';
import orderBy from 'lodash/orderBy';
import { Dropdown } from 'office-ui-fabric-react/lib/Dropdown';
import DimensionLists from '../../components/DimensionLists';
import Module from '../../components/Module';
import ModuleHeader from '../../components/ModuleHeader';
import ModuleContentList from '../../components/ModuleContentList';
import PageTitle from '../../components/PageTitle';
import Main from '../../components/Main';
import SearchBar from '../../components/SearchBar';
import { fetchDimensionLists } from '../../actions/dimensionList';
import { fetchTasks } from '../../actions/tasks';
import useAPI from '../../hooks/useAPI';
import DimensionListsTasksList from '../../components/DimensionListsTasksList';
import { dimensionListTypes } from '../../constants';
import theme from '../../theme.style';
import styles from './DimensionListView.style';

const DimensionListView = () => {
  const dispatch = useDispatch();

  const [filterState, setFilterState] = useState({});
  const [sortColumn, setSortColumn] = useState('');
  const [sortAscending, setSortAscending] = useState(false);

  const selector = state => {
    const dList = get(state, 'pagination.dimensionLists.results', []).map(listId => {
      return get(state, `entities.dimensionLists.${listId}`);
    });
    const filtered = dList.filter(el => {
      return el != null;
    });
    const sortedItems =
      sortColumn === 'associatedContracts'
        ? orderBy(
            filtered,
            x => {
              return x[sortColumn]?.length;
            },
            [sortAscending ? 'asc' : 'desc'],
          )
        : orderBy(filtered, [sortColumn], [sortAscending ? 'asc' : 'desc']);
    if (sortedItems.length < get(state, `pagination.dimensionLists.totalCount`, 0)) {
      return sortedItems.concat(new Array(1));
    }
    return sortedItems;
  };

  const tasks = useSelector(state => Object.values(get(state, 'entities.tasks', {})));

  const totalCount = useSelector(state => get(state, `pagination.dimensionLists.totalCount`, 0));

  const dimensionListAPI = useAPI(
    () => {
      return fetchDimensionLists(filterState);
    },
    [filterState],
    {
      fetchOnMount: false,
      fetchOnDependenciesChange: true,
      selector,
    },
  );

  useEffect(() => {
    dispatch(fetchTasks(['dimensionlists']));
  }, [dispatch]);

  const updateSearchFilter = async sValue => {
    setFilterState(prevState => ({
      ...prevState,
      searchCriteria: sValue,
      start: 0,
    }));
  };

  const onListTypeChanged = async (event, option) => {
    setFilterState(prevState => ({
      ...prevState,
      listType: option.key,
      start: 0,
    }));
  };

  const handleClear = () => updateSearchFilter('');
  const handleSearch = sValue => updateSearchFilter(sValue);

  const onScrollBottom = useCallback(() => {
    if (dimensionListAPI.isLoading) {
      return;
    }

    if (dimensionListAPI.data.length === totalCount) {
      return;
    }

    setFilterState(prevState => ({
      ...prevState,
      start: dimensionListAPI.data.length - 1,
      count: 20,
    }));
  }, [dimensionListAPI]);

  const filterOptions = [{ key: '', text: 'All List Types' }].concat(dimensionListTypes);

  return (
    <>
      <PageTitle>Lists</PageTitle>
      <Main>
        {tasks.length !== 0 && (
          <Module>
            <ModuleHeader>Action Required</ModuleHeader>
            <ModuleContentList>
              <DimensionListsTasksList items={tasks} isHeaderVisible={false} noBorderBottom />
            </ModuleContentList>
          </Module>
        )}
        <div className={styles.searchSection}>
          <SearchBar
            placeholder="Search lists"
            ariaLabel="Search"
            underlined
            onChange={handleSearch}
            onClear={handleClear}
            data-testid="search-input"
            className={styles.search}
          />
          <Dropdown
            placeholder="All List Types"
            label="List Type"
            options={filterOptions}
            className={styles.filter}
            styles={{
              title: { backgroundColor: theme.palette.neutralWhite },
              dropdown: { fontWeight: 'normal' },
              dropdownItem: { fontWeight: 'normal' },
            }}
            onChange={onListTypeChanged}
            data-testid="listTypeDropdown"
          />
        </div>

        {!!filterState?.searchCriteria && (
          <div className={styles.resultsMessage}>
            <Text as="h4" block>
              {dimensionListAPI.data.length}{' '}
              {dimensionListAPI.data.length === 1 ? 'Result' : 'Results'}
            </Text>
            {dimensionListAPI.data.length === 0 && (
              <Text as="p" role="alert" block className={styles.noResultsSentence}>
                Try searching by list name, dimension name, contract ID, list item name (with list
                type selected)
              </Text>
            )}
          </div>
        )}

        {(!filterState?.searchCriteria || dimensionListAPI.data.length) && (
          <Module data-testid="dimensionListTable">
            <ModuleContentList className={styles.listContainer}>
              <DimensionLists
                items={dimensionListAPI.data}
                reviewerItems={tasks}
                onShouldVirtualize={() => false}
                shimmered
                className={styles.dimensionList}
                enableShimmer={dimensionListAPI.isLoading && !filterState?.start}
                sortColumn={sortColumn}
                sortAscending={sortAscending}
                setSortColumn={setSortColumn}
                setSortAscending={setSortAscending}
                onScrollBottom={onScrollBottom}
              />
            </ModuleContentList>
          </Module>
        )}
      </Main>
    </>
  );
};

export default DimensionListView;
